import React, { useState, useEffect } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import PropTypes from 'prop-types';
import { useLocation, useHistory, useParams } from 'react-router-dom';
import classNames from 'classnames';

//material ui
import { Collapse, CircularProgress } from '@material-ui/core';

import { FormattedMessageWrappedInSpan } from '../../misc';
import GDPRHint from '../GDPRHint';
import { formatSnWithMaybeUndefinedVal } from '../../../utils/common';
import GetStravaAccessTokenMutation from '../../../mutations/GetStravaAccessTokenMutation';
import stravaButton from '../../../static/images/btn_strava_connect.svg';

import environment from '../../../environment';
import StravaActivitiesSelectionQueryRenderer from './StravaActivitiesSelectionQueryRenderer';

const ExtendedSearchStravaTab = (props) => {
  const [showSearchBtn, setShowSearchBtn] = useState(false);
  const [stravaAccessToken, setStravaAccessToken] = useState();
  const [showStravaAuthorizationError, setShowStravaAuthorizationError] = useState(false);
  const [showStravaNoActivitiesError, setShowStravaNoActivitiesError] = useState(false);
  const [stravaActivityData, setStravaActivityData] = useState({
    stravaActivityId: '',
    stravaActivityStartDate: '',
  });
  const [isLoading, setIsLoading] = useState(false);

  const location = useLocation();
  const history = useHistory();
  const params = useParams();

  const noStravaActivitiesFoundCallback = () => {
    setShowStravaNoActivitiesError(true);
  };

  const availableStravaActivityId =
    stravaActivityData.stravaActivityId || params.stravaActivityId;

  const setUpStravaActivity = (newActivityData) => {
    setStravaActivityData({
      stravaActivityId: newActivityData.stravaActivityId,
      stravaActivityStartDate: newActivityData.stravaActivityStartDate,
    });
    setShowSearchBtn(true);
  };

  //after Strava authorization
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const authorizationCode = searchParams.get('code');

    if (authorizationCode && !stravaAccessToken) {
      setIsLoading(true);
      GetStravaAccessTokenMutation.commit(
        environment,
        authorizationCode,
        (error, response) => {
          if (!error) {
            setStravaAccessToken(response.accessToken);
            setShowStravaAuthorizationError(false);
            setShowStravaNoActivitiesError(false);
            setIsLoading(false);
          } else {
            setShowStravaAuthorizationError(true);
          }
        }
      );
    }
  }, [location.search, stravaAccessToken]);

  useEffect(() => {
    if (stravaAccessToken) {
      history.replace({
        pathname: location.pathname,
        search: 'strvAuth=true',
      });
    }
  }, [stravaAccessToken, history, location.pathname]);

  const handleConnectToStrava = () => {
    setShowSearchBtn(false);

    // TODO add refresh token for previous search if backend supports this feature
    // for now, previous search results are cleaned
    const pathname = window.location.pathname;
    let cleanPathname;
    const isStrava = window.location.pathname.includes('strava');

    if (isStrava) {
      const gpsParamsStartIndex = pathname.indexOf('/gps');
      cleanPathname = pathname.substring(0, gpsParamsStartIndex);
    }

    const redirectUri =
      window.location.origin + (cleanPathname ? cleanPathname : window.location.pathname);

    // TODO remove read_all scope to only ask for activity access
    const scope = 'activity:read_all';

    const authUrl = `https://www.strava.com/oauth/authorize?client_id=${STRAVA_CLIENT_ID}&redirect_uri=${redirectUri}&response_type=code&scope=${scope}`;
    window.location.href = authUrl;
  };

  const handleUploadGpxFile = () => {
    const { eventId, startNumber, onNavigateTo } = props;
    let formattedstartNumber = formatSnWithMaybeUndefinedVal(startNumber);
    if (
      stravaAccessToken &&
      Boolean(stravaActivityData.stravaActivityId) &&
      Boolean(stravaActivityData.stravaActivityStartDate)
    ) {
      onNavigateTo(
        `/event/${eventId}/gps/strava/${
          stravaActivityData.stravaActivityId
        }/${window.btoa(
          stravaActivityData.stravaActivityStartDate
        )}/${formattedstartNumber}/?codeStrava=${stravaAccessToken}`
      );
    }
  };

  return (
    <div className="extended-search-gpssearch-container">
      <div>
        <p>
          <FormattedMessageWrappedInSpan
            id="extendedSearchGpsSearchTab.ownSearch.descriptionStrava"
            defaultMessage="Connect to Strava, choose an activity and we will show your photos"
          />
        </p>
        <div className="gps-search-columns">
          <div
            className={classNames('gps-search-column', {
              'gps-search-column_selected': stravaAccessToken,
            })}
          >
            <Collapse in={showStravaAuthorizationError || showStravaNoActivitiesError}>
              <span className="gps-search-column-label">
                {showStravaAuthorizationError && (
                  <FormattedMessageWrappedInSpan
                    id="extendedSearchGpsSearchTab.ownSearch.stravaErrorAuth"
                    defaultMessage="Authorization failed. Try again"
                    className="typography bold-text warning-text"
                  />
                )}
                {showStravaNoActivitiesError && (
                  <FormattedMessageWrappedInSpan
                    id="extendedSearchGpsSearchTab.ownSearch.stravaErrorNoActivities"
                    defaultMessage="No activities found for this account. Try to log in to another one"
                    className="typography bold-text warning-text"
                  />
                )}
              </span>
            </Collapse>
            {stravaAccessToken &&
              !showStravaAuthorizationError &&
              !showStravaNoActivitiesError && (
                <StravaActivitiesSelectionQueryRenderer
                  accessToken={stravaAccessToken}
                  isAuthorizationError={showStravaAuthorizationError}
                  noStravaActivitiesFoundCallback={noStravaActivitiesFoundCallback}
                  setUpStravaActivity={setUpStravaActivity}
                  eventDates={props.eventDates}
                />
              )}
            {(!stravaAccessToken || showStravaNoActivitiesError || showStravaAuthorizationError) && (
              <div>
                {isLoading && !showStravaAuthorizationError ? (
                  <CircularProgress size={40} thickness={3} />
                ) : (
                  <img
                    src={stravaButton}
                    onClick={handleConnectToStrava}
                    data-testid="strava-gps-upload-btn"
                    className="strava-gps-upload-btn"
                  />
                )}
              </div>
            )}
            <Collapse
              in={Boolean(availableStravaActivityId)}
              className={classNames('small-hint-under-frame', {
                'mt-20': Boolean(availableStravaActivityId),
              })}
            >
              <a
                target="_blank"
                href={`https://www.strava.com/activities/${availableStravaActivityId}`}
                rel="noopener noreferrer"
              >
                <FormattedMessageWrappedInSpan
                  id="extendedSearchGpsSearchTab.ownSearch.viewOnStrava"
                  defaultMessage="View on Strava"
                  className="typography bold-text"
                />
              </a>
            </Collapse>
          </div>
        </div>
        <Collapse
          in={showSearchBtn}
          className={classNames({
            'mt-20': showSearchBtn,
          })}
        >
          <button className="btn btn-default primary-new" onClick={handleUploadGpxFile}>
            <FormattedMessageWrappedInSpan
              id="extendedSearchGpsSearchTab.ownSearch.searchButton"
              defaultMessage="Search"
            />
          </button>
        </Collapse>
      </div>
      <GDPRHint className="mt-30" />
    </div>
  );
};

ExtendedSearchStravaTab.propTypes = {
  eventId: PropTypes.number,
  startNumber: PropTypes.string,
  onNavigateTo: PropTypes.func,
  eventDates: PropTypes.object,
};

export { ExtendedSearchStravaTab };
export default createFragmentContainer(ExtendedSearchStravaTab, {
  eventDates: graphql`
    fragment ExtendedSearchStravaTab_eventDates on Event {
      beginsAt
      endsAt
    }
  `,
});
